import { useEffect, useState } from "react";
import axios from "axios";

export default function ApiDocs({ apiBaseUrl }: { apiBaseUrl: string }) {
  const [docsHtml, setDocsHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        setLoading(true);
        // Make an authenticated request to fetch the Swagger UI
        const response = await axios.get(`${apiBaseUrl}/docs`, {
          responseType: "text",
        });

        // Handle the HTML response
        setDocsHtml(response.data);
      } catch (err) {
        console.error("Failed to load API docs:", err);
        setError(
          axios.isAxiosError(err) && err.response?.status === 403
            ? "You don't have permission to view the API documentation. Superuser privileges are required."
            : "Failed to load API documentation."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, [apiBaseUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-16">
        <span className="loading loading-spinner loading-lg text-primary mr-3"></span>
        <span className="text-lg">Loading API documentation...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">API Documentation</h1>
        <div role="alert" className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2 2m2-2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="font-bold">Error</h3>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (docsHtml) {
    // Create a fullscreen iframe with the docs content
    // Note: This uses dangerouslySetInnerHTML which needs special consideration for security
    return (
      <div className="h-screen flex flex-col">
        <div className="flex-1 min-h-[800px]">
          <iframe
            srcDoc={docsHtml}
            title="API Documentation"
            className="w-full h-full border-none"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      </div>
    );
  }

  return null;
}
